import React, { CSSProperties, useCallback } from 'react';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import cx from 'classnames';
import Landscape from 'assets/images/invitations/landscape.svg';
import Portrait from 'assets/images/invitations/portrait.svg';
import WithPhotosIcon from 'assets/images/invitations/with-photos.svg';
import WithoutPhotosIcon from 'assets/images/invitations/wihtout-photos.svg';
import ToggleV2 from '@zola/zola-ui/src/components/ToggleV2';
import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';
import { isSeoColorType } from 'cards/constants/seoColors';
import { Motif, Shape } from 'cards/components/CardListing/types';

import { ModalOptions } from 'components/common/types';
import ProvisionalBtnImage from '../../ProvisionalComponents/ProvisionalBtnImage';
import ProvisionalSelectBtnImage from '../../ProvisionalComponents/ProvisionalSelectBtnImage';

import styles from './styles.module.less';
import { getLabel } from '../../utils';
import { ActiveFilters, IndividualVisibilityCheck } from '../FilterTypes';

export const MotifMenu: React.FC<MotifMenuProps> = ({
  optionList,
  activeValue,
  updateFilter,
  isMobile,
  isHoliday,
}) => {
  if (!optionList) return <></>;
  function sortMotifs() {
    const motifOptions = [...optionList];
    return motifOptions.sort((a, b) => {
      if (a.display_order < b.display_order) {
        return -1;
      }
      if (a.display_order > b.display_order) {
        return 1;
      }
      return 0;
    });
  }

  return (
    <>
      {isMobile && <div className={styles.fitlerTitle}>{isHoliday ? 'Occasion' : 'Style'}</div>}
      <div className={cx({ [styles.filterSection]: isMobile })}>
        {sortMotifs().map(item => (
          <CheckboxField
            className={styles.filterCheckbox}
            id={item.key.toLowerCase()}
            key={item.name}
            label={item.name}
            checked={activeValue && activeValue.includes(item.key.toUpperCase())}
            onChange={() => {
              const newMotifs = [...activeValue];
              if (newMotifs.includes(item.key.toUpperCase())) {
                newMotifs.splice(newMotifs.indexOf(item.key.toUpperCase()), 1);
              } else {
                newMotifs.push(item.key.toUpperCase());
              }
              updateFilter('motifs', newMotifs);
            }}
            meta={{ touched: true }}
          />
        ))}
      </div>
    </>
  );
};

type MotifMenuProps = {
  optionList: Motif[];
  activeValue: string[];
  isMobile?: boolean;
  isHoliday?: boolean;
  updateFilter: (filter: 'motifs', value: string[]) => void;
};

export const ColorMenu = ({
  optionList,
  activeValue,
  activeSeoValue,
  updateFilter,
  isMobile,
}: ColorMenuProps): JSX.Element => {
  return (
    <>
      {isMobile && <div className={styles.fitlerTitle}>Color</div>}
      <div className={cx({ [styles.filterSection]: isMobile })}>
        {optionList.map(item => {
          const isSeoColor = isSeoColorType(item);
          return (
            <CheckboxField
              className={styles.filterCheckbox}
              label={
                <div className={styles.colorTagContainer}>
                  <div
                    className={cx(styles.colorTag, styles[item.toLowerCase()], {
                      [styles.colorTagWhite]: item.toLowerCase() === 'white',
                    })}
                  />
                  {item && <div>{getLabel(item)}</div>}
                </div>
              }
              id={item}
              key={item}
              checked={
                isSeoColor
                  ? activeSeoValue && activeSeoValue.includes(item)
                  : activeValue && activeValue.includes(item)
              }
              onChange={() => {
                if (isSeoColor) {
                  const newSeoMultiColor = [...activeSeoValue];
                  if (newSeoMultiColor.includes(item)) {
                    newSeoMultiColor.splice(newSeoMultiColor.indexOf(item), 1);
                  } else {
                    newSeoMultiColor.push(item);
                  }

                  updateFilter('seo_colors', newSeoMultiColor);
                } else {
                  const newMultiColor = [...activeValue];
                  if (newMultiColor.includes(item)) {
                    newMultiColor.splice(newMultiColor.indexOf(item), 1);
                  } else {
                    newMultiColor.push(item);
                  }
                  updateFilter('multiColor', newMultiColor);
                }
              }}
              meta={{ touched: true }}
            />
          );
        })}
      </div>
    </>
  );
};

type ColorMenuProps = {
  optionList: string[];
  activeValue: string[];
  activeSeoValue: string[];
  isMobile?: boolean;
  updateFilter: (filter: 'multiColor' | 'seo_colors', value: string[]) => void;
};

export const MagnetMenu = ({
  activeValue,
  updateFilter,
  isMobile,
}: MagnetMenuProps): JSX.Element => {
  return (
    <>
      <div className={cx('visible-xs', styles.fitlerTitle)}>Magnet</div>
      <div
        className={cx(styles.toggleContainer, {
          [styles.filterSection]: isMobile,
        })}
      >
        <div className={styles.toggleText}>Only show designs that come in a Magnet.</div>
        <ToggleV2
          id="magnet-toggle"
          value={activeValue}
          onChange={() => updateFilter('has_magnet', !activeValue)}
          ariaLabel="Has Magnet"
          withIcons
        />
      </div>
    </>
  );
};

type MagnetMenuProps = {
  activeValue: boolean;
  updateFilter: (filter: 'has_magnet', value: boolean) => void;
  isMobile: boolean;
};

export const CustomPhotoMenu = ({
  activeFilters,
  updateFilter,
  individualVisibilityCheck,
}: CustomPhotoMenuProps): JSX.Element => (
  <>
    {individualVisibilityCheck.has_custom_photo && (
      <CheckboxField
        className={styles.filterCheckbox}
        label={
          <span>
            Only show designs <b>with photos</b>
          </span>
        }
        id="custom_photo"
        key="custom_photo"
        checked={!!activeFilters.has_custom_photo}
        onChange={() => {
          updateFilter('has_custom_photo', !activeFilters.has_custom_photo);
        }}
        meta={{ touched: true }}
      />
    )}
    {individualVisibilityCheck.has_no_custom_photo && (
      <CheckboxField
        className={styles.filterCheckbox}
        label={
          <span>
            Only show designs <b>without photos</b>
          </span>
        }
        id="no_custom_photo"
        key="no_custom_photo"
        type="checkbox"
        checked={!!activeFilters.has_no_custom_photo}
        onChange={() => {
          updateFilter('has_no_custom_photo', !activeFilters.has_no_custom_photo);
        }}
        meta={{ touched: true }}
      />
    )}
  </>
);

type CustomPhotoMenuProps = {
  activeFilters: Pick<ActiveFilters, 'has_custom_photo' | 'has_no_custom_photo'>;
  updateFilter: (filter: 'has_custom_photo' | 'has_no_custom_photo', value: boolean) => void;
  individualVisibilityCheck: IndividualVisibilityCheck;
};

export const CustomFoilMenu = ({
  activeValue,
  updateFilter,
  showModal,
  isMobile,
}: CustomFoilMenuProps): JSX.Element => {
  const modalStyle = isMobile
    ? ({
        borderRadius: '4px',
        bottom: '0',
        position: 'absolute',
      } as CSSProperties)
    : ({
        borderRadius: '4px',
      } as CSSProperties);

  return (
    <>
      <div className={cx('visible-xs', styles.fitlerTitle)}>Foil</div>
      <div
        className={cx(styles.toggleContainer, {
          [styles.filterSection]: isMobile,
        })}
      >
        <div className={styles.toggleText}>
          {'Only show designs with foil. '}
          <LinkV2
            inline
            role="button"
            onClick={() =>
              showModal(
                'FILTER_FOIL_MODAL',
                {},
                { size: 'sm', contentStyle: modalStyle, fullScreenOnMobile: true }
              )
            }
          >
            What&apos;s foil?
          </LinkV2>
        </div>
        <ToggleV2
          id="custom-foil-toggle"
          value={activeValue}
          onChange={() => updateFilter('has_foil', !activeValue)}
          ariaLabel="Has Foil"
          withIcons
        />
      </div>
    </>
  );
};

type CustomFoilMenuProps = {
  activeValue: boolean;
  updateFilter: (filter: 'has_foil', value: boolean) => void;
  showModal: (
    modalType: string,
    modalProps: Record<string, unknown>,
    modalOptions: ModalOptions
  ) => void;
  isMobile: boolean;
};

export const ShapeMenu: React.FC<ShapeMenuProps> = ({
  activeValue,
  optionList,
  updateFilter,
  isMobile,
}: ShapeMenuProps): JSX.Element => {
  const updateLocalFilter = useCallback(
    (newVal: string) => {
      let selectedShapes = [...activeValue];

      if (selectedShapes.includes(newVal)) {
        selectedShapes = [];
      } else {
        selectedShapes = [newVal];
      }

      updateFilter('silhouettes', selectedShapes);
    },
    [activeValue, updateFilter]
  );

  return (
    <>
      {isMobile && <div className={styles.fitlerTitle}>Shape</div>}
      <div className={cx({ [styles.filterSection]: isMobile })}>
        <div className={styles.shapesContent}>
          {optionList.map(key => {
            return (
              <ProvisionalSelectBtnImage
                key={key.label}
                label={key.label}
                icon={key.icon}
                isActive={activeValue?.includes(key.value)}
                onClick={() => updateLocalFilter(key.value)}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

type ShapeMenuProps = {
  activeValue: string[];
  updateFilter: (filter: 'silhouettes', value: string[]) => void;
  optionList: Shape[];
  isMobile: boolean;
};

export const LetterpressMenu = ({
  activeValue,
  updateFilter,
  showModal,
  isMobile,
}: LetterpressMenuProps): JSX.Element => {
  const modalStyle = isMobile
    ? ({
        borderRadius: '4px',
        bottom: '0',
        position: 'absolute',
      } as CSSProperties)
    : ({
        borderRadius: '4px',
      } as CSSProperties);

  return (
    <>
      <div className={cx('visible-xs', styles.fitlerTitle)}>Letterpress</div>
      <div
        className={cx(styles.toggleContainer, {
          [styles.filterSection]: isMobile,
        })}
      >
        <div className={styles.toggleText}>
          {'Only show designs that can be letterpress printed. '}
          <LinkV2
            inline
            role="button"
            onClick={() =>
              showModal(
                'FILTER_LETTERPRESS_MODAL',
                {},
                { size: 'sm', contentStyle: modalStyle, fullScreenOnMobile: true }
              )
            }
          >
            What&apos;s letterpress?
          </LinkV2>
        </div>
        <ToggleV2
          id="letterpress-toggle"
          value={activeValue}
          onChange={() => updateFilter('is_letterpress', !activeValue)}
          ariaLabel="Has Letterpress"
          withIcons
        />
      </div>
    </>
  );
};

type LetterpressMenuProps = {
  activeValue: boolean;
  updateFilter: (filter: 'is_letterpress', value: boolean) => void;
  showModal: (
    modalType: string,
    modalProps: Record<string, unknown>,
    modalOptions: ModalOptions
  ) => void;
  isMobile: boolean;
};

export const ReceptionMenu = ({
  activeValue,
  updateFilter,
  isMobile,
}: ReceptionMenuProps): JSX.Element => {
  return (
    <>
      <div className={cx('visible-xs', styles.fitlerTitle)}>
        Reception Only <div className={styles.newBadge}>New</div>
      </div>
      <div
        className={cx(styles.toggleContainer, {
          [styles.filterSection]: isMobile,
        })}
      >
        <div className={styles.toggleText}>Only show elopement &#38; vow renewal designs.</div>
        <ToggleV2
          id="reception-toggle"
          value={activeValue}
          onChange={() => updateFilter('is_vow_renewal', !activeValue)}
          ariaLabel="Is vow renewal"
          withIcons
        />
      </div>
    </>
  );
};

type ReceptionMenuProps = {
  activeValue: boolean;
  updateFilter: (filter: 'is_vow_renewal', value: boolean) => void;
  isMobile: boolean;
};

export const OrientationMenu = ({
  activeFilters,
  updateFilter,
}: OrientationMenuProps): JSX.Element => (
  <>
    <div className={styles.fitlerTitle}>Orientation</div>
    <div className={styles.filterSection}>
      <CheckboxField
        className={styles.filterCheckbox}
        label="Landscape"
        id="landscape"
        type="checkbox"
        checked={activeFilters.is_landscape}
        onChange={() => updateFilter('is_landscape', !activeFilters.is_landscape)}
        meta={{ touched: true }}
      />
      <CheckboxField
        className={styles.filterCheckbox}
        label="Portrait"
        id="portrait"
        type="checkbox"
        checked={activeFilters.is_portrait}
        onChange={() => updateFilter('is_portrait', !activeFilters.is_portrait)}
        meta={{ touched: true }}
      />
    </div>
  </>
);

type OrientationMenuProps = {
  activeFilters: Pick<ActiveFilters, 'is_landscape' | 'is_portrait'>;
  updateFilter: (filter: 'is_landscape' | 'is_portrait', value: boolean) => void;
};

export const CtdOrientationMenu = ({
  activeFilters,
  updateFilter,
}: CtdOrientationMenuProps): JSX.Element => (
  <>
    <div className={styles.filterSection}>
      <CheckboxField
        className={styles.filterCheckbox}
        label="Landscape"
        id="landscape"
        type="checkbox"
        checked={activeFilters.is_landscape}
        onChange={() => updateFilter('is_landscape', !activeFilters.is_landscape)}
        meta={{ touched: true }}
      />
      <CheckboxField
        className={styles.filterCheckbox}
        label="Portrait"
        id="portrait"
        type="checkbox"
        checked={activeFilters.is_portrait}
        onChange={() => updateFilter('is_portrait', !activeFilters.is_portrait)}
        meta={{ touched: true }}
      />
    </div>
  </>
);

type CtdOrientationMenuProps = {
  activeFilters: Pick<ActiveFilters, 'is_landscape' | 'is_portrait'>;
  updateFilter: (filter: 'is_landscape' | 'is_portrait', value: boolean) => void;
};

export const FeaturedArtistMenu = ({
  activeFilters,
  updateFilter,
}: FeaturedArtistMenuProps): JSX.Element => {
  return (
    <>
      <div className={styles.fitlerTitle}>Featured Artist</div>
      <div className={cx(styles.toggleContainer, styles.filterSection)}>
        <div className={styles.toggleText}>Only show designs created by our featured artists.</div>
        <ToggleV2
          id="featured-artist-toggle"
          value={activeFilters.featured_artist}
          onChange={() => updateFilter('featured_artist', !activeFilters.featured_artist)}
          ariaLabel="Featured Artists"
          withIcons
        />
      </div>
    </>
  );
};

type FeaturedArtistMenuProps = {
  activeFilters: Pick<ActiveFilters, 'featured_artist'>;
  updateFilter: (filter: 'featured_artist', value: boolean) => void;
};

// TODO --> update with toggle buttons
export const MobileCustomPhotoMenu = ({
  activeFilters,
  updateFilter,
  individualVisibilityCheck,
}: MobileCustomPhotoMenuProps): JSX.Element => (
  <>
    <div className={styles.fitlerTitle}>Custom Photo</div>
    <div className={styles.imgFilterSection}>
      {individualVisibilityCheck.has_custom_photo && (
        <ProvisionalBtnImage
          label="Designs with Photos"
          icon={WithPhotosIcon}
          isActive={!!activeFilters.has_custom_photo}
          onClick={() => {
            updateFilter('has_custom_photo', !activeFilters.has_custom_photo);
          }}
        />
      )}
      {individualVisibilityCheck.has_no_custom_photo && (
        <ProvisionalBtnImage
          label="Designs without Photos"
          icon={WithoutPhotosIcon}
          isActive={!!activeFilters.has_no_custom_photo}
          onClick={() => {
            updateFilter('has_no_custom_photo', !activeFilters.has_no_custom_photo);
          }}
        />
      )}
    </div>
  </>
);

type MobileCustomPhotoMenuProps = {
  activeFilters: Pick<ActiveFilters, 'has_custom_photo' | 'has_no_custom_photo'>;
  updateFilter: (filter: 'has_custom_photo' | 'has_no_custom_photo', value: boolean) => void;
  individualVisibilityCheck: IndividualVisibilityCheck;
};

// TODO --> update with toggle buttons
export const MobileOrientationMenu = ({
  activeFilters,
  updateFilter,
}: MobileOrientationMenuProps): JSX.Element => (
  <>
    <div className={styles.fitlerTitle}>Orientation</div>
    <div className={styles.imgFilterSection}>
      <ProvisionalBtnImage
        label="Landscape"
        icon={Landscape}
        isActive={activeFilters.is_landscape}
        onClick={() => updateFilter('is_landscape', !activeFilters.is_landscape)}
      />
      <ProvisionalBtnImage
        label="Portrait"
        icon={Portrait}
        isActive={activeFilters.is_portrait}
        onClick={() => updateFilter('is_portrait', !activeFilters.is_portrait)}
      />
    </div>
  </>
);

type MobileOrientationMenuProps = {
  activeFilters: Pick<ActiveFilters, 'is_landscape' | 'is_portrait'>;
  updateFilter: (filter: 'is_landscape' | 'is_portrait', value: boolean) => void;
};
